import { Tooltip, TooltipTrigger, TooltipPopup } from "../ui/tooltip";
import { ChevronDownIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import type { BreadcrumbLeafActions } from "./GlobalBreadcrumb";

export function BreadcrumbLeafActionButton({
  label,
  actions,
}: {
  readonly label: string;
  readonly actions: BreadcrumbLeafActions;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancel = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [actions, label],
  );

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={`Thread actions for ${label}`}
            aria-haspopup="menu"
            className="group flex min-h-touch min-w-0 items-center gap-1 rounded-sm text-left font-medium tracking-tight outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-8"
            onBlur={cancel}
            onClick={(event) => {
              if (event.detail > 1) return;
              cancel();
              const rect = event.currentTarget.getBoundingClientRect();
              const open = () => actions.onContextMenu?.({ x: rect.left, y: rect.bottom + 4 });
              const chevron = (event.target as Element).closest("[data-thread-title-chevron]");
              if (event.detail === 0 || chevron || !actions.onRename) open();
              else timer.current = setTimeout(open, 500);
            }}
            onDoubleClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
              if ((event.target as Element).closest("[data-thread-title-chevron]")) return;
              cancel();
              actions.onRename?.();
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              cancel();
              actions.onContextMenu?.({ x: event.clientX, y: event.clientY });
            }}
          >
            <span className="truncate">{label}</span>
            <ChevronDownIcon
              data-thread-title-chevron
              aria-hidden="true"
              className="size-3.5 shrink-0 text-muted-foreground sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100 motion-safe:transition-opacity"
            />
          </button>
        }
      />
      <TooltipPopup>{label}</TooltipPopup>
    </Tooltip>
  );
}
